// ============================================================
// useLibraryPanel - the Character Library panel's state.
//
// All of it lives at the panel, above both views, on purpose: switching
// between Folder and This session unmounts one view and mounts the
// other, and the selection, counts, room and last note each survive
// that round trip.
// ============================================================
import { useEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';
import { applySelection, toggleGroupSelection } from '../../utils/listSelection';

/** Paths picked in the folder tree - `Group/Name`, or `Name` at the root. */
function useFolderPicking() {
  const [picked, setPicked] = useState<Set<string>>(new Set());
  // The shelf's own draw order, so a shift-range walks what you see.
  const order = useRef<string[]>([]);
  const anchor = useRef<string | null>(null);
  const toggle = (path: string, e: ReactMouseEvent, remove = false) => {
    const out = applySelection({ order: order.current, selected: picked, anchor: anchor.current }, path, {
      shift: e.shiftKey,
      remove,
    });
    setPicked(out.selected);
    anchor.current = out.anchor;
  };
  const selectGroup = (paths: string[]) => setPicked((prev) => toggleGroupSelection(prev, paths));
  const reportOrder = (paths: string[]) => {
    order.current = paths;
  };
  const clear = () => {
    setPicked(new Set());
    anchor.current = null;
  };
  return { picked, toggle, selectGroup, reportOrder, clear };
}

/** The hovered row and where it was when the pointer arrived (the preview is placed off it). */
function useHover() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [hoverRect, setHoverRect] = useState<DOMRect | null>(null);
  return { hoveredId, setHoveredId, hoverRect, setHoverRect };
}

/**
 * The panel's state: which view, the session list's selection (with its
 * shift-range anchor), the folder's picks, the hover, the staging room,
 * the per-row counts and the last verb's note.
 */
export function useLibraryPanel() {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /** Which source the menu is showing. The folder is the durable one. */
  const [view, setView] = useState<'folder' | 'library'>('folder');
  const folder = useFolderPicking();
  const hover = useHover();
  const [stageRoomId, setStageRoomId] = useState('');
  // HOW MANY OF EACH. Loading three of somebody used to mean the spawn
  // dialog, which only appears for a single selection - so a bulk load
  // could not make copies at all (v8.26.0).
  const [counts, setCounts] = useState<Record<string, number>>({});
  /** What the last verb did, in one line. */
  const [note, setNote] = useState<string | null>(null);
  const lastClickedId = useRef<string | null>(null);
  const selection = { selectedIds, setSelectedIds, lastClickedId };
  return { view, setView, selection, folder, hover, stageRoomId, setStageRoomId, counts, setCounts, note, setNote };
}

export type LibraryPanel = ReturnType<typeof useLibraryPanel>;

/** Is this press inside a modal dialog that is not the panel's own container (one opened over it)? */
function inDialogAbove(target: EventTarget | null, panel: HTMLElement): boolean {
  // A press always lands on a node; `closest` is on elements only (a text node has none).
  const dialog = (target as Partial<HTMLElement> | null)?.closest?.('[aria-modal="true"]');
  return !!dialog && !dialog.contains(panel);
}

/**
 * Close the panel on a pointer press anywhere outside it, while open.
 *
 * POINTERDOWN, NOT MOUSEDOWN. The map calls `preventDefault()` on its
 * pointerdown - Build and Deploy both do - and preventing a pointer
 * event suppresses the compatibility mouse event that follows it. So
 * clicking the battlefield never closed this menu, while clicking
 * anything else did (v8.26.0).
 *
 * A press in a modal dialog over the panel is that dialog's, not outside:
 * the library picker opens from here as a portal on the body, and a press
 * in it closed the panel and the picker with it (2026-10-20). A dialog the
 * panel itself sits in does not count - its own backdrop still closes it.
 */
export function useCloseOnOutsidePointer(
  isOpen: boolean,
  panelRef: RefObject<HTMLDivElement | null>,
  onClose: () => void,
) {
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const panel = panelRef.current;
      if (!panel || panel.contains(e.target as Node) || inDialogAbove(e.target, panel)) return;
      onClose();
    };
    document.addEventListener('pointerdown', handleClick);
    return () => document.removeEventListener('pointerdown', handleClick);
  }, [isOpen, onClose, panelRef]);
}
